<template>
  <dialog-modal-fi
    ref="dialogModal"
    class="add-to-memo-list-dialog-fi"
    data-test="modal-add-to-memo-list"
  >
    <template #header>
      <headline-fi
        tag="h2"
        :size="24"
      >
        <template v-if="addCart">
          {{ $dict.get('memo-List.Add-To-Memo-List-Dialog.Headline-Cart') }}
        </template>
        <template v-else>
          {{ $dict.get('memo-List.Add-To-Memo-List-Dialog.Headline-Product') }}
        </template>
      </headline-fi>
    </template>

    <template #body>
      <button-fi
        type="button"
        class="button-fi__primary add-to-memo-list-dialog-fi__new-list-button"
        data-test="create-memo-list"
        @click="newList"
      >
        {{ $dict.get('memo-List.Add-To-Memo-List-Dialog.New-List') }}
      </button-fi>

      <fieldset id="add-to-memo-list-dialog-fi__group">
        <label
          v-if="showNewList"
          class="add-to-memo-list-dialog-fi__list preferNonApp"
        >
          <component
            :is="multiple ? 'checkbox-fi' : 'radio-fi'"
            ref="newNameRadio"
            v-model="selectedList"
            :value="newListCode"
            name="add-to-memo-list-dialog-fi__group"
            class="add-to-memo-list-dialog-fi__list-selector"
            wrapped
            wrapper-tag="div"
          />
          <div class="add-to-memo-list-dialog-fi__list-name">
            <text-input-fi
              ref="newNameInput"
              v-model.trim="newListName"
              :placeholder="namePlaceholder"
              :maxlength="nameMaxLength"
              data-test="memo-list-name-input"
              show-max-length
              transparent
              clear
            />
          </div>
        </label>

        <label
          v-for="list in getMemoLists"
          :key="list.code"
          class="add-to-memo-list-dialog-fi__list preferNonApp"
        >
          <component
            :is="multiple ? 'checkbox-fi' : 'radio-fi'"
            v-model="selectedList"
            :value="list.code"
            name="add-to-memo-list-dialog-fi__group"
            class="add-to-memo-list-dialog-fi__list-selector"
            wrapped
            wrapper-tag="div"
          />
          <div class="add-to-memo-list-dialog-fi__list-name">
            {{ list.name }}
          </div>
          <div class="add-to-memo-list-dialog-fi__list-count">
            {{
              $dict.get(
                list.totalItems !== 1
                  ? 'memo-List.List-Product-Count-Plural'
                  : 'memo-List.List-Product-Count-Singular',
                [list.totalItems],
              )
            }}
          </div>
        </label>
      </fieldset>
    </template>

    <template #footer>
      <div class="add-to-memo-list-dialog-fi__actions">
        <button-fi
          type="button"
          :class="{
            'button-fi__tertiary': true,
            'button-fi__xs-streched': true,
            'edit-memo-list-dialog-fi__replace-button': true,
          }"
          @click="close"
        >
          {{ $dict.get('online-Shop.Global-Labels.Cancel') }}
        </button-fi>
        <button-fi
          type="button"
          class="button-fi__primary button-fi__xs-streched edit-memo-list-dialog-fi__merge-button"
          data-test="add-to-memo-list"
          :disabled="selectedList === null || selectedList.length === 0"
          @click="confirm"
        >
          {{ $dict.get('memo-List.Add-To-Memo-List-Dialog.Confirm') }}
        </button-fi>
      </div>
    </template>
    <template #button>
      <icon-fi icon="close" />
    </template>
  </dialog-modal-fi>
</template>

<script>
import {
  ButtonFi,
  CheckboxFi,
  DialogModalFi,
  HeadlineFi,
  IconFi,
  RadioFi,
  TextInputFi,
} from 'atoms';
import memoListMixin from 'components/memo-list/memoListMixin';
import ADD_CART_OR_PRODUCT_TO_MEMO_LIST from 'gql/addCartOrProductToMemoList.gql';
import GET_MEMO_LISTS from 'gql/getMemoLists.gql';
import { getIsIds } from 'utils/idsTools';

export default {
  name: 'AddToMemoListDialogFi',
  components: {
    ButtonFi,
    CheckboxFi,
    DialogModalFi,
    HeadlineFi,
    IconFi,
    RadioFi,
    TextInputFi,
  },
  mixins: [memoListMixin],
  props: {
    multiple: Boolean,
    addCart: Boolean,
    product: {
      type: String,
      default: null,
    },
    quantity: {
      type: Number,
      default: null,
    },
  },
  emits: ['added-to-memolist', 'error-occured', 'error-occured'],
  data() {
    return {
      isShown: false,
      name: '',
      description: null,
      getMemoLists: [],
      showNewList: false,
      newListName: '',
      selectedList: null,
      newListCode: 'new_list',
    };
  },
  created() {
    if (this.multiple) {
      this.selectedList = [];
    }
  },
  apollo: {
    getMemoLists: {
      query: GET_MEMO_LISTS,
      variables() {
        return {
          baseSiteId: this.$fischer.baseSiteId(),
          accessToken: this.$fischer.user.accessToken(),
          userId: this.$fischer.user.id(),
        };
      },
      skip() {
        return !this.isShown;
      },
      error() {
        this.$emit('error-occured', 'Could not load memo-lists');
        this.close();
      },
    },
  },
  methods: {
    async newList() {
      this.showNewList = true;
      await this.$nextTick();
      this.$refs.newNameInput.focus();
      if (this.multiple) {
        if (!this.selectedList.includes(this.newListCode)) {
          this.selectedList.push(this.newListCode);
        }
      } else {
        this.selectedList = this.newListCode;
      }
    },
    async show() {
      this.selectedList = this.multiple ? [] : null;
      this.showNewList = false;
      this.newListName = '';

      this.$refs.dialogModal.open();
      this.isShown = true;
    },
    async confirm() {
      this.close();
      try {
        const memoListCodes = this.multiple ? this.selectedList : [this.selectedList];

        await this.$apollo.mutate({
          mutation: ADD_CART_OR_PRODUCT_TO_MEMO_LIST,
          variables: {
            baseSiteId: this.$fischer.baseSiteId(),
            accessToken: this.$fischer.user.accessToken(),
            userId: this.$fischer.user.id(),
            isIds: getIsIds(),
            memoListCodes,
            newListName: this.newListName || this.namePlaceholder,
            addCart: this.addCart,
            product: this.product,
            quantity: this.quantity,
          },
        });

        const listNames = memoListCodes.map((code) => {
          if (code === 'new_list') {
            return this.newListName;
          }
          const oldList = this.getMemoLists.find((list) => list.code === code);
          return oldList ? oldList.name : code;
        });
        const plural = this.multiple && this.selectedList.length > 1;
        let message;
        if (this.addCart) {
          if (plural) {
            message = 'memo-List.Notifications.Added-Cart-To-List-Plural';
          } else {
            message = 'memo-List.Notifications.Added-Cart-To-List-Singular';
          }
        } else if (plural) {
          message = 'memo-List.Notifications.Added-Product-To-List-Plural';
        } else {
          message = 'memo-List.Notifications.Added-Product-To-List-Singular';
        }
        this.$store.dispatch('notifications/openToastNotification', {
          name: 'added-to-memo-list',
          message: { value: this.$dict.get(message, [`<strong>${listNames.join(', ')}</strong>`]) },
          type: 'success',
          icon: 'check',
        });

        if (memoListCodes.includes('new_list')) {
          if (this.addCart) {
            this.sendEvent('memo_list_create_from_cart');
          } else {
            this.sendEvent('memo_list_create');
          }
        }

        this.$emit('added-to-memolist');
      } catch (error) {
        console.error(error);
        this.$emit('error-occured', 'Could not add cart to memo-list');
      }
    },
    close() {
      this.$refs.dialogModal.close();
      this.isShown = false;
    },
  },
};
</script>

<style lang="scss">
@import './add-to-memo-list-dialog-fi';
</style>
